import apiClient from '@shared/services/apiClient';
import type {
  ProcedureTemplateDto,
  SaveProcedureTemplateRequest,
  BulkOperationResultDto,
  ClinicDeactivationScanRequest,
  ClinicDeactivationPreviewDto,
  ClinicDeactivationExecuteRequest,
  AutoHierarchyScanRequest,
  AutoHierarchyPreviewDto,
  AutoHierarchyExecuteRequest,
  CutoverOperationLogDto,
  ClinicMatrixPreviewDto,
  ClinicMatrixRecheckDto,
  ClinicMatrixExecuteRequest,
  ClinicMatrixCapabilityExecuteRequest,
  ClinicCodesCapabilityExecuteRequest,
  GeocodingBatchPreviewDto,
  GeocodingBatchStatusDto,
  LabCorpImportPreviewDto,
  LabCorpImportResultDto,
  ClinicImportPreviewDto,
  ClinicImportResultDto,
  ClinicImportRowDto,
  JobSiteImportPreviewDto,
  JobSiteImportExecuteResultDto,
  JobSiteImportRowDto,
} from '../types/cutover';

const BASE = '/admin/cutover';

export const cutoverApi = {
  // === Procedure Templates ===

  getTemplates: async (): Promise<ProcedureTemplateDto[]> => {
    const response = await apiClient.get(`${BASE}/procedure-templates`);
    return response.data;
  },

  getTemplateById: async (id: number): Promise<ProcedureTemplateDto> => {
    const response = await apiClient.get(`${BASE}/procedure-templates/${id}`);
    return response.data;
  },

  createTemplate: async (request: SaveProcedureTemplateRequest): Promise<{ id: number }> => {
    const response = await apiClient.post(`${BASE}/procedure-templates`, request);
    return response.data;
  },

  updateTemplate: async (id: number, request: SaveProcedureTemplateRequest): Promise<void> => {
    await apiClient.put(`${BASE}/procedure-templates/${id}`, request);
  },

  deleteTemplate: async (id: number): Promise<void> => {
    await apiClient.delete(`${BASE}/procedure-templates/${id}`);
  },

  previewTemplate: async (id: number): Promise<BulkOperationResultDto> => {
    const response = await apiClient.post(`${BASE}/procedure-templates/${id}/preview`);
    return response.data;
  },

  applyTemplate: async (id: number): Promise<BulkOperationResultDto> => {
    const response = await apiClient.post(`${BASE}/procedure-templates/${id}/apply`);
    return response.data;
  },

  importTemplate: async (request: SaveProcedureTemplateRequest): Promise<{ id: number }> => {
    const response = await apiClient.post(`${BASE}/procedure-templates/import`, request);
    return response.data;
  },

  exportTemplate: async (id: number): Promise<ProcedureTemplateDto> => {
    const response = await apiClient.get(`${BASE}/procedure-templates/${id}/export`);
    return response.data;
  },

  // === Procedure Snapshot ===

  getProcedureSnapshot: async (): Promise<unknown> => {
    const response = await apiClient.get(`${BASE}/procedure-snapshot`);
    return response.data;
  },

  // === Clinic Deactivation ===

  scanForDeactivation: async (request: ClinicDeactivationScanRequest): Promise<ClinicDeactivationPreviewDto> => {
    const response = await apiClient.post(`${BASE}/clinic-deactivation/scan`, request);
    return response.data;
  },

  executeDeactivation: async (request: ClinicDeactivationExecuteRequest): Promise<BulkOperationResultDto> => {
    const response = await apiClient.post(`${BASE}/clinic-deactivation/execute`, request);
    return response.data;
  },

  getDeactivationHistory: async (page = 1, pageSize = 20): Promise<CutoverOperationLogDto[]> => {
    const response = await apiClient.get(`${BASE}/clinic-deactivation/history`, { params: { page, pageSize } });
    return response.data;
  },

  // === Auto-Hierarchy ===

  scanForHierarchy: async (request: AutoHierarchyScanRequest): Promise<AutoHierarchyPreviewDto> => {
    const response = await apiClient.post(`${BASE}/auto-hierarchy/scan`, request);
    return response.data;
  },

  executeHierarchy: async (request: AutoHierarchyExecuteRequest): Promise<BulkOperationResultDto> => {
    const response = await apiClient.post(`${BASE}/auto-hierarchy/execute`, request);
    return response.data;
  },

  getHierarchyHistory: async (page = 1, pageSize = 20): Promise<CutoverOperationLogDto[]> => {
    const response = await apiClient.get(`${BASE}/auto-hierarchy/history`, { params: { page, pageSize } });
    return response.data;
  },

  // === Operation Log ===

  getOperationLog: async (operationType?: string, page = 1, pageSize = 20): Promise<CutoverOperationLogDto[]> => {
    const response = await apiClient.get(`${BASE}/operation-log`, { params: { operationType, page, pageSize } });
    return response.data;
  },

  // === Clinic Matrix Import ===

  uploadClinicMatrix: async (file: File): Promise<ClinicMatrixPreviewDto> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post(`${BASE}/clinic-matrix/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000,
    });
    return response.data;
  },

  /** SLV2-3377 — Exists + inferred type for one (clinic, location) pair chosen after the upload. */
  recheckClinicMatrixRow: async (clinicId: number, customerLocationId: number): Promise<ClinicMatrixRecheckDto> => {
    const response = await apiClient.post(`${BASE}/clinic-matrix/recheck`, { clinicId, customerLocationId });
    return response.data;
  },

  executeClinicMatrixImport: async (request: ClinicMatrixExecuteRequest): Promise<BulkOperationResultDto> => {
    const response = await apiClient.post(`${BASE}/clinic-matrix/execute`, request);
    return response.data;
  },

  getClinicMatrixHistory: async (page = 1, pageSize = 20): Promise<CutoverOperationLogDto[]> => {
    const response = await apiClient.get(`${BASE}/clinic-matrix/history`, { params: { page, pageSize } });
    return response.data;
  },

  executeCapabilityUpdates: async (request: ClinicMatrixCapabilityExecuteRequest): Promise<BulkOperationResultDto> => {
    const response = await apiClient.post(`${BASE}/clinic-matrix/capabilities/execute`, request);
    return response.data;
  },

  exportMismatches: async (file: File): Promise<Blob> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post(`${BASE}/clinic-matrix/export-mismatches`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      responseType: 'blob',
      timeout: 120000,
    });
    return response.data;
  },

  // === Clinic Matrix Export ===

  exportClinicMatrix: async (): Promise<Blob> => {
    const response = await apiClient.get(`${BASE}/clinic-matrix/export`, {
      responseType: 'blob',
      timeout: 120000,
    });
    return response.data;
  },

  /** Cutover C8: blank import template with the exact sheet/column layout the upload expects. */
  downloadClinicMatrixTemplate: async (): Promise<Blob> => {
    const response = await apiClient.get(`${BASE}/clinic-matrix/template`, { responseType: 'blob' });
    return response.data;
  },

  // === Clinic Codes ===

  executeClinicCodesUpdates: async (request: ClinicCodesCapabilityExecuteRequest): Promise<BulkOperationResultDto> => {
    const response = await apiClient.post(`${BASE}/clinic-codes/capabilities/execute`, request);
    return response.data;
  },

  // === Geocoding Batch ===

  getGeocodingPreview: async (): Promise<GeocodingBatchPreviewDto> => {
    const response = await apiClient.get(`${BASE}/geocoding/preview`);
    return response.data;
  },

  runGeocoding: async (batchSize?: number): Promise<void> => {
    const params = batchSize != null ? { batchSize } : undefined;
    await apiClient.post(`${BASE}/geocoding/run`, null, { params });
  },

  getGeocodingStatus: async (): Promise<GeocodingBatchStatusDto> => {
    const response = await apiClient.get(`${BASE}/geocoding/status`);
    return response.data;
  },

  stopGeocoding: async (): Promise<void> => {
    await apiClient.post(`${BASE}/geocoding/stop`);
  },

  /** Cutover C7: Excel of every clinic still lacking coordinates, with reasons. */
  exportGeocodingExceptions: async (): Promise<Blob> => {
    const response = await apiClient.get(`${BASE}/geocoding/exceptions/export`, {
      responseType: 'blob',
      timeout: 120000,
    });
    return response.data;
  },

  // === Location Geocoding Batch ===

  getLocationGeocodingPreview: async (): Promise<GeocodingBatchPreviewDto> => {
    const response = await apiClient.get(`${BASE}/location-geocoding/preview`);
    return response.data;
  },

  runLocationGeocoding: async (batchSize?: number): Promise<void> => {
    const params = batchSize != null ? { batchSize } : undefined;
    await apiClient.post(`${BASE}/location-geocoding/run`, null, { params });
  },

  getLocationGeocodingStatus: async (): Promise<GeocodingBatchStatusDto> => {
    const response = await apiClient.get(`${BASE}/location-geocoding/status`);
    return response.data;
  },

  stopLocationGeocoding: async (): Promise<void> => {
    await apiClient.post(`${BASE}/location-geocoding/stop`);
  },

  /** Cutover C7: Excel of every customer location still lacking coordinates, with customer/XMIDs and reasons. */
  exportLocationGeocodingExceptions: async (): Promise<Blob> => {
    const response = await apiClient.get(`${BASE}/location-geocoding/exceptions/export`, {
      responseType: 'blob',
      timeout: 120000,
    });
    return response.data;
  },

  // === LabCorp Import ===

  previewLabCorpImport: async (file: File): Promise<LabCorpImportPreviewDto> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post(`${BASE}/labcorp/preview`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  executeLabCorpImport: async (token: string): Promise<LabCorpImportResultDto> => {
    const response = await apiClient.post(`${BASE}/labcorp/execute`, { token, skipDuplicates: true });
    return response.data;
  },

  // ── Clinic Bulk Import (SLV2-2819) ──

  downloadClinicImportTemplate: async (): Promise<Blob> => {
    const response = await apiClient.get(`${BASE}/clinic-import/template`, { responseType: 'blob' });
    return response.data;
  },

  uploadClinicImport: async (file: File): Promise<ClinicImportPreviewDto> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post(`${BASE}/clinic-import/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000,
    });
    return response.data;
  },

  executeClinicImport: async (token: string): Promise<ClinicImportResultDto> => {
    const response = await apiClient.post(`${BASE}/clinic-import/execute`, { token });
    return response.data;
  },

  updateClinicImportRow: async (token: string, row: ClinicImportRowDto): Promise<ClinicImportPreviewDto> => {
    const response = await apiClient.put(`${BASE}/clinic-import/row`, { token, row });
    return response.data;
  },

  // === JobSite Bulk Import (SLV2-4126) ===

  downloadJobSiteImportTemplate: async (): Promise<Blob> => {
    const response = await apiClient.get(`${BASE}/jobsite-import/template`, { responseType: 'blob' });
    return response.data;
  },

  uploadJobSiteImport: async (file: File): Promise<JobSiteImportPreviewDto> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post(`${BASE}/jobsite-import/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000,
    });
    return response.data;
  },

  updateJobSiteImportRow: async (token: string, row: JobSiteImportRowDto): Promise<JobSiteImportPreviewDto> => {
    const response = await apiClient.put(`${BASE}/jobsite-import/row`, { token, row });
    return response.data;
  },

  /** Creates the next batch of Ready rows; call until `remaining` is 0. */
  executeJobSiteImportBatch: async (token: string, batchSize: number): Promise<JobSiteImportExecuteResultDto> => {
    const response = await apiClient.post(`${BASE}/jobsite-import/execute`, { token, batchSize }, { timeout: 120000 });
    return response.data;
  },

  downloadJobSiteImportResults: async (token: string): Promise<Blob> => {
    const response = await apiClient.get(`${BASE}/jobsite-import/results`, {
      params: { token },
      responseType: 'blob',
    });
    return response.data;
  },
};